import { FEATURE_FLAGS } from '@/constants';
import { loadManagerDashboardData } from '@/services';
import { quickLog } from '@/utils/console-log';
import { useEffect, useRef } from 'react';
import { useGlobalStore } from '../global-store';
import { usePathname } from 'next/navigation';
import type { IUser } from '@/types';

/**
 * Initializes manager dashboard data for manager routes.
 *
 * Responsibilities:
 * - Guard by feature flag, auth, store-config, and route.
 * - Load manager dashboard data from services layer.
 * - Commit mapped manager summary into store.
 * - Avoid duplicate requests for same manager+pathname.
 */
export const useInitializeManagerStoreData = ({ user }: { user: IUser | null }) => {
  // Pull only the store actions/selectors required for manager data loading.
  const { fetchManagerData, setError, setManagersTeamSummary } = useGlobalStore();

  // Current URL path used for route-aware manager data loading.
  const pathName = usePathname();

  // Cache key tracker used to avoid duplicate fetches for the same
  // manager+pathname combination during re-renders.
  const didInitManagerRef = useRef<string | null>(null);

  // ================================
  // Manager Bootstrap Effect
  // ================================
  // Runs when manager-relevant dependencies change. The effect intentionally
  // contains all guard clauses so we can quickly return before any network work.
  useEffect(() => {
    // Cancellation flag protects against setting store state after unmount or
    // after dependency changes while async calls are still in flight.
    let isCancelled = false;

    // Initializes manager dashboard data under guarded conditions.
    // Flow:
    // 1. Validate feature/auth/config/route prerequisites.
    // 2. De-duplicate by manager+path cache key.
    // 3. Load data from services layer.
    // 4. Commit summary to store unless cancelled.
    const loadManagerData = async () => {
      // Guard: manager endpoints can be globally disabled by feature flags.
      if (!FEATURE_FLAGS.ENABLE_MANAGER_API_ENDPOINTS) return;

      // Guard: user identity is required for manager-scoped API requests.
      if (!user?.uuid) return;

      // Guard: store-level toggle for suppressing manager API traffic.
      if (!fetchManagerData) {
        quickLog.api('Fetch manager data flag is false, skipping API calls');
        return;
      }

      // Guard: only run on manager routes.
      if (!pathName.includes('/manager')) return;

      // De-duplication: avoid refetching for an already-initialized route/user pair.
      const cacheKey = `${user.uuid}:${pathName}`;
      if (didInitManagerRef.current === cacheKey) return;
      didInitManagerRef.current = cacheKey;

      try {
        // Grouped logs provide readable traces while debugging manager bootstrap.
        quickLog.group('Manager dashboard accessed, loading managed users data');

        // Service boundary handles API orchestration + mapping contract.
        const result = await loadManagerDashboardData(user.uuid);

        // Bail out if effect lifecycle is no longer current.
        if (isCancelled) return;

        quickLog.data('Fetched manager summary:', result.managersTeamSummary);
        quickLog.data('Fetched manager shared scorecards:', result.sharedScorecards);
        quickLog.groupEnd();

        // Commit only the store slice currently consumed by manager dashboards.
        setManagersTeamSummary(result.managersTeamSummary);
      } catch (err) {
        // Preserve existing behavior: log detailed error and surface fallback.
        console.error('Error fetching manager data:', err);
        setError(err ? err : 'Unknown error occurred');
      }
    };

    // Fire-and-forget bootstrap invocation from effect.
    loadManagerData();

    // Cleanup marks this execution as stale to prevent post-unmount writes.
    return () => {
      isCancelled = true;
    };
  }, [fetchManagerData, pathName, setError, setManagersTeamSummary, user?.uuid]);
};
